import React, { useState, useEffect } from 'react';
import './Pages.css';

const OngoingClaims = ({ userRole }) => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    fetchOngoingClaims();
  }, []);

  const fetchOngoingClaims = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/claims');
      const data = await response.json();

      if (data.success) {
        // Filter Under Review and Approved claims
        const ongoingClaims = data.claims.filter(claim => {
          const status = claim.Status?.toLowerCase() || '';
          return status === 'under review' || status === 'approved';
        });
        setClaims(ongoingClaims);
      }
    } catch (err) {
      console.error('Error fetching claims:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClaim = async (claimId) => {
    setShowModal(true);
    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/claims/${claimId}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedClaim(data.claim);
        setNewStatus(data.claim.Status || 'Under Review');
      }
    } catch (err) {
      console.error('Error fetching claim details:', err);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      alert('Please select a status');
      return;
    }

    setUpdatingStatus(true);
    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/claims/${selectedClaim.ClaimId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Claim status updated successfully');
        setShowModal(false);
        fetchOngoingClaims();
      } else {
        alert(data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
    setUpdatingStatus(false);
  };

  const getStatusBadgeClass = (status) => {
    if (!status) return 'under-review';
    const statusLower = status.toLowerCase();
    if (statusLower === 'under review') return 'under-review';
    if (statusLower === 'approved') return 'approved';
    return 'under-review';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  if (loading) {
    return <div className="page-container"><div className="loading">Loading ongoing claims...</div></div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Ongoing Claims</h1>
          <p>Under review and approved - {claims.length} claim{claims.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Claim #</th>
              <th>Client</th>
              <th>Policy #</th>
              <th>Product</th>
              <th>Incident Date</th>
              <th>Claim Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {claims.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center' }}>No ongoing claims</td>
              </tr>
            ) : (
              claims.map((claim) => (
                <tr key={claim.ClaimId}>
                  <td><strong>{claim.ClaimNumber}</strong></td>
                  <td>{claim.ClientName}</td>
                  <td>{claim.PolicyNumber}</td>
                  <td>{claim.ProductName}</td>
                  <td>{new Date(claim.DateOfIncident).toLocaleDateString()}</td>
                  <td>{formatCurrency(claim.ClaimAmount)}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(claim.Status)}`}>
                      {claim.Status}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="btn btn-small btn-view"
                      onClick={() => handleViewClaim(claim.ClaimId)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Claim Modal */}
      {showModal && selectedClaim && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Claim Details - {selectedClaim.ClaimNumber}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Claim Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Claim Number:</label>
                    <span><strong>{selectedClaim.ClaimNumber}</strong></span>
                  </div>
                  <div className="detail-item">
                    <label>Claim Type:</label>
                    <span>{selectedClaim.ClaimType}</span>
                  </div>
                  <div className="detail-item">
                    <label>Date of Incident:</label>
                    <span>{new Date(selectedClaim.DateOfIncident).toLocaleDateString()}</span>
                  </div>
                  <div className="detail-item">
                    <label>Date Reported:</label>
                    <span>{new Date(selectedClaim.DateReported).toLocaleDateString()}</span>
                  </div>
                  <div className="detail-item">
                    <label>Claim Amount:</label>
                    <span><strong>{formatCurrency(selectedClaim.ClaimAmount)}</strong></span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span>
                      <span className={`status-badge ${getStatusBadgeClass(selectedClaim.Status)}`}>
                        {selectedClaim.Status}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="detail-item full-width">
                  <label>Claim Narration:</label>
                  <p className="narration-text">{selectedClaim.ClaimNarration}</p>
                </div>
              </div>

              {userRole === 1 && (
                <div className="detail-section">
                  <h3>Update Status</h3>
                  <div className="form-group">
                    <label>Change Status:</label>
                    <select 
                      value={newStatus} 
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="status-select"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Under Review">Under Review</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Settled">Settled</option>
                    </select>
                    <button 
                      className="btn btn-primary"
                      onClick={handleUpdateStatus}
                      disabled={updatingStatus}
                      style={{ marginTop: '10px' }}
                    >
                      {updatingStatus ? 'Updating...' : 'Update Status'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OngoingClaims;
